import {gql} from '@apollo/client';
import {areSetsEqual, noop, objectFromEntries} from '@benchling/js-utils';
import React, {useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {requestAccess} from 'auth/RequestAccess';
import {pluralize} from 'BenchlingUtil';
import {
  ActionMethod,
  ActionOrigin,
  ANALYTICS_MODULE,
  COMMON_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  ParametersForDocumentMethod,
  ParametersForDocumentMethod_procedureStepExecutionInstances_ProcedureStepExecutionInstanceNode as PSEINode,
} from 'notebook/integrations/procedures/add-parameters-to-table/__generated/ParametersForDocumentMethod';
import {
  ConditionReplicateSelectionModalFormControls,
  ConditionReplicateWithPMEI,
  DataParameterSelectionModalFormControls,
  EquipmentParameterSelectionModalFormControls,
  MaterialInputParameterSelectionModalFormControls,
  MaterialOutputParameterSelectionModalFormControls,
} from 'notebook/integrations/procedures/add-parameters-to-table/ParameterStructuredTableParameterSelectionModalFormControls';
import {
  PARAMETER_TABLE_MODE_TO_DISPLAY_NAME,
  ParameterStructuredTable,
} from 'notebook/integrations/procedures/ParameterStructuredTableAdapter';
import {
  PARAMETER_TABLE_MODES_THAT_ALLOW_PLATE_PARAMETERS,
  ParameterInfo,
  ParameterStructuredTableMode,
  ParameterStructuredTableModeToTypeName,
  ParameterStructuredTableModeToTypeNamePlural,
} from 'notebook/integrations/procedures/ParameterStructuredTableConstants';
import {
  assembleDataParameterInfos,
  assembleEquipmentParameterInfos,
  assembleMaterialInputParameterInfos,
  assembleMaterialOutputParameterInfos,
  assembleParameterInfos,
} from 'notebook/integrations/procedures/ProceduresUtil';
import {ParameterSettings} from 'procedures/design/step-config/ProcedureStepConfigEditorContext';
import {parseParameterSettingsFromNetwork} from 'procedures/network/parseProcedureFromNetwork';
import {ParameterUsages} from 'procedures/ProcedureDomainTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {fillPlanMethodVersionWithPlannedValuesAndConfirmationMethods} from 'procedures/utils/fillPlanMethodVersionWithPlannedValuesAndConfirmationMethods';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import {StepperV2} from 'ui-library/stepper/Stepper';
import {assertNever} from 'util/Types';

export const ADD_PARAMETERS_FROM_METHOD_MODAL_TYPE = 'add_parameters_from_method';

// Public interface
export interface ParameterStructuredTableParameterSelectionModalProps {
  onRequestSubmit: (addParametersCommand: {parameters: Array<ParameterInfo>}) => void;
  onRequestClose: () => void;
  parameterTableMode: ParameterStructuredTableMode;
  // Document Info
  fileId?: string;
  isConfig: boolean;
  /** If this is in config, use the Parameter info already provided from the editor */
  parameterSettings?: ParameterSettings;
  /** Adds additional info about used parameters and finds any re-used parameters */
  parameterUsages?: ParameterUsages;
  /** If adding to an existing table, provide the table we'll be inserting into */
  destinationTable?: ParameterStructuredTable;
  sessionId: string; // For analytics
}

export const PARAMETERS_FOR_METHOD_FROM_DOCUMENT_QUERY = gql`
  query ParametersForDocumentMethod($docId: StrictString!) @skipAudit @engComponent(name: DATA_INPUT) {
    procedureStepExecutionInstances(documentId: $docId) {
      ... on ProcedureStepExecutionInstanceNode {
        id
        methodExecutionInstance {
          ... on ProcedureMethodExecutionInstanceNode {
            id
            procedureRunConditionReplicate {
              id
              replicateNumber
              procedureRunCondition {
                id
                name
              }
            }
            parameterConfirmationMethods {
              parameterId
              confirmationMethod
            }
            ...ProcedureMethodExecutionInstancePlannedValuesGraphQL
            planMethodDefinitionVersion {
              ... on ProcedurePlanMethodDefinitionVersionNode {
                id
                ...ProcedureMethodDefinitionVersionParameterVersionsGraphQL
                materialInputSettings {
                  templateValues {
                    id
                    material
                    amount
                  }
                }
              }
              ...InaccessibleInfoGraphQL
            }
            templateMethodVersion {
              ... on ProcedureMethodDefinitionVersionNode {
                id
              }
              ... on InaccessibleInfoNode {
                ...InaccessibleInfoGraphQL
              }
            }
            procedureRun {
              ... on ProcedureRunNode {
                id
                procedureType
              }
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('ProcedureMethodExecutionInstancePlannedValues').fragment}
  ${getFragmentWrapper('ProcedureMethodDefinitionVersionParameterVersions').fragment}
`;

export function trackAddParametersModalAnalyticsEvent(
  event: analytics.AnalyticsEvent,
  table: ParameterStructuredTable | undefined,
  otherMetadata: {
    session_id: string;
    action_origin: ActionOrigin;
    action_method: ActionMethod | undefined;
  } & Record<string, unknown>
): void {
  const payload = {
    modal_type: ADD_PARAMETERS_FROM_METHOD_MODAL_TYPE,

    file_id: table?.fileId ?? null,
    is_config: table?.isConfig ?? null,
    structured_table_api_identifier: table?.apiId ?? null,
    structured_table_provenance_uuid: table?.provenanceUuid ?? null,
    structured_table_mode: table?.metadata.mode ?? null,
    structured_table_metadata: JSON.stringify(table?.metadata ?? null),
    structured_table_num_rows: table?.rows.length ?? null,
    structured_table_num_columns: table?.columns.length ?? null,

    ...otherMetadata,
  };
  analytics.trackEventWithModule(event, ANALYTICS_MODULE.PROCEDURES, payload);
}

export default function ParameterStructuredTableParameterSelectionModal(
  props: ParameterStructuredTableParameterSelectionModalProps
): JSX.Element {
  return props.isConfig ? (
    <ConfigParameterSelectionModal {...props} />
  ) : (
    <ExecutionParameterSelectionModal {...props} />
  );
}

function useParameterSelectionModalCommonUtils(
  props: ParameterStructuredTableParameterSelectionModalProps,
  options: {
    selectedParameters: Array<ParameterInfo>;
    shouldDisableSubmit?: boolean;
  }
): {
  trackAnalyticsEvent: (event: analytics.AnalyticsEvent, extraMetadata?: Record<string, unknown>) => void;
  title: string;
  buttons: Array<ModalButton>;
} {
  const {parameterTableMode, destinationTable} = props;
  const {selectedParameters, shouldDisableSubmit} = options;
  const numberOfSelectedParameters = selectedParameters.length;
  function trackAnalyticsEvent(
    event: analytics.AnalyticsEvent,
    extraMetadata: Record<string, unknown> = {}
  ): void {
    trackAddParametersModalAnalyticsEvent(event, destinationTable, {
      session_id: props.sessionId,
      action_method: undefined,
      action_origin: ActionOrigin.NOTEBOOK_UNDETERMINED_STRUCTURED_TABLE,
      selected_parameter_ids: selectedParameters.map((param) => param.paramId),
      num_selected_parameters: numberOfSelectedParameters,
      ...extraMetadata,
    });
  }
  const title = destinationTable
    ? `Add ${ParameterStructuredTableModeToTypeNamePlural[parameterTableMode]} to table`
    : `Insert ${ParameterStructuredTableModeToTypeName[parameterTableMode]} table`;

  const submitText = `${destinationTable ? `Add` : `Insert`}${
    numberOfSelectedParameters > 0 ? ` ${numberOfSelectedParameters}` : ''
  } ${pluralize(
    numberOfSelectedParameters,
    ParameterStructuredTableModeToTypeName[parameterTableMode],
    ParameterStructuredTableModeToTypeNamePlural[parameterTableMode]
  )}`;
  const buttons: Array<ModalButton> = [
    {
      submit: true,
      text: submitText,
      disabled: numberOfSelectedParameters === 0 || shouldDisableSubmit,
      style: ButtonStyle.SECONDARY,
      dataTestAttrs: {component: 'ParameterStructuredTableParameterSelectionModal', element: 'addButton'},
    },
  ];
  return {
    trackAnalyticsEvent,
    title,
    buttons,
  };
}

function ConfigParameterSelectionModal(
  props: ParameterStructuredTableParameterSelectionModalProps
): JSX.Element {
  const {isConfig, parameterTableMode, parameterSettings} = props;
  const [selectedParameters, setSelectedParameters] = useState<Array<ParameterInfo>>([]);
  const {trackAnalyticsEvent, title, buttons} = useParameterSelectionModalCommonUtils(props, {
    selectedParameters,
  });

  function handleSubmit(): void {
    trackAnalyticsEvent(COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL);
    props.onRequestSubmit({
      parameters: selectedParameters,
    });
  }

  function handleClose(): void {
    trackAnalyticsEvent(COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL);
    props.onRequestClose();
  }

  return (
    <Modal
      title={title}
      onRequestSubmit={handleSubmit}
      onRequestClose={handleClose}
      buttons={buttons}
      dataTestAttrs={{component: 'ParameterStructuredTableParameterSelectionModal', element: 'root'}}
    >
      {parameterSettings ? (
        <ParameterSelectionTable
          parameterSettings={parameterSettings}
          selectedParameters={selectedParameters}
          onChangeSelectedParameters={setSelectedParameters}
          parameterTableMode={parameterTableMode}
          isConfig={isConfig}
          parameterUsages={props.parameterUsages}
          shouldShowValueColumn={true}
        />
      ) : (
        <div>Unable to retrieve Parameters.</div>
      )}
    </Modal>
  );
}

function ExecutionParameterSelectionModal(
  props: ParameterStructuredTableParameterSelectionModalProps
): JSX.Element {
  const {isConfig, fileId, parameterTableMode, parameterUsages} = props;
  const [selectedParameters, setSelectedParameters] = useState<Array<ParameterInfo>>([]);
  const [selectedConditionReplicates, setSelectedConditionReplicates] = useState<
    Array<ConditionReplicateWithPMEI>
  >([]);
  const queryResult = useBenchlingQuery<ParametersForDocumentMethod>(
    PARAMETERS_FOR_METHOD_FROM_DOCUMENT_QUERY,
    {variables: {docId: fileId}}
  );
  const hasMultipleConditionReplicates =
    queryResult.type === QueryResultType.SUCCESS &&
    queryResult.data.procedureStepExecutionInstances.length > 1;
  // Values may differ between condition replicates in multi-batch
  const includeParameterValues = !hasMultipleConditionReplicates;
  const procedureType =
    queryResult.type === QueryResultType.SUCCESS
      ? (queryResult.data.procedureStepExecutionInstances
          .find((psei): psei is PSEINode => !isInaccessible(psei))
          ?.methodExecutionInstance.procedureRun.procedureType?.toLowerCase() ?? null)
      : null;
  const {trackAnalyticsEvent, title, buttons} = useParameterSelectionModalCommonUtils(props, {
    selectedParameters,
    shouldDisableSubmit: selectedConditionReplicates.length === 0 && hasMultipleConditionReplicates,
  });

  function handleClose(): void {
    trackAnalyticsEvent(COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL, {
      num_selected_condition_replicates: hasMultipleConditionReplicates
        ? selectedConditionReplicates.length
        : 1,
      procedure_type: procedureType,
    });
    props.onRequestClose();
  }
  if (queryResult.type === QueryResultType.ERROR) {
    return (
      <Modal
        title={title}
        onRequestSubmit={noop}
        onRequestClose={handleClose}
        buttons={buttons}
        dataTestAttrs={{component: 'ParameterStructuredTableParameterSelectionModal', element: 'root'}}
      >
        There was an error retrieving the {ParameterStructuredTableModeToTypeName[parameterTableMode]} list.
      </Modal>
    );
  }

  if (queryResult.type === QueryResultType.PENDING) {
    return (
      <Modal
        title={title}
        onRequestSubmit={noop}
        onRequestClose={handleClose}
        buttons={buttons}
        dataTestAttrs={{component: 'ParameterStructuredTableParameterSelectionModal', element: 'root'}}
      >
        <div style={{display: 'flex', justifyContent: 'center'}}>
          <CenteredHorizontalThrobber />
        </div>
      </Modal>
    );
  }

  const stepExecutionInstances = queryResult.data.procedureStepExecutionInstances;
  const accessiblePSEIs = stepExecutionInstances.filter((psei): psei is PSEINode => !isInaccessible(psei));
  if (accessiblePSEIs.length === 0) {
    return <div>No accessible steps found.</div>;
  }
  const representativePSEI = accessiblePSEIs[0];
  if (isInaccessible(representativePSEI.methodExecutionInstance.templateMethodVersion)) {
    if (representativePSEI.methodExecutionInstance.templateMethodVersion.canRequestAccess) {
      requestAccess(representativePSEI.methodExecutionInstance.templateMethodVersion);
    }
    return (
      <Modal
        title={title}
        onRequestSubmit={noop}
        onRequestClose={handleClose}
        buttons={buttons}
        dataTestAttrs={{component: 'ParameterStructuredTableParameterSelectionModal', element: 'root'}}
      >
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text={`Cannot ${title.toLowerCase()} because you do not have access to the template ${getProcedureUIString(ProceduresStringKeys.PROCEDURE, representativePSEI.methodExecutionInstance.procedureRun.procedureType, {stringCase: StringCase.LOWER})}.`}
        />
      </Modal>
    );
  }
  assert(
    !isInaccessible(representativePSEI.methodExecutionInstance.planMethodDefinitionVersion),
    'Expecting plan method definition version to be accessible'
  );

  const representativeParameterIds = getParameterIds(representativePSEI);
  // Parameters from any 2 PSEIs/ConditionReplicates are "equivalent" for the purposes of parameter
  // display and selection, but they will have different ParameterValues depending on the condition-replicate
  assert(
    accessiblePSEIs.every((psei) => areParameterIdsEqual(getParameterIds(psei), representativeParameterIds)),
    'Expected all PSEIs to have the same parameters so we can just look at the first one for parameter selection.'
  );
  const parameterSettings = getParameterSettingsFromPSEI(representativePSEI, includeParameterValues);

  const conditionReplicates: Array<ConditionReplicateWithPMEI> = accessiblePSEIs.map(
    ({methodExecutionInstance: {id, procedureRunConditionReplicate}}) => ({
      ...procedureRunConditionReplicate,
      procedureMethodExecutionInstanceId: id,
    })
  );

  function handleSubmit(): void {
    trackAnalyticsEvent(COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL, {
      num_selected_condition_replicates: hasMultipleConditionReplicates
        ? selectedConditionReplicates.length
        : 1,
      procedure_type: procedureType,
    });
    const parameterInfosByPMEI = objectFromEntries(
      accessiblePSEIs.map((psei) => {
        const {id: pmeiId} = psei.methodExecutionInstance;
        const parameterSettings = getParameterSettingsFromPSEI(psei);
        const parameterInfos: Array<ParameterInfo> = assembleParameterInfos(
          parameterTableMode,
          parameterSettings
        );
        return [pmeiId, parameterInfos];
      })
    );
    // If there are multiple condition-replicates, use the selected condition replicates, otherwise just use
    // the lone condition-replicate
    const usedConditionReplicates = hasMultipleConditionReplicates
      ? selectedConditionReplicates
      : [conditionReplicates[0]];
    const selectedParametersWithConditions: Array<ParameterInfo> = [];
    for (const selectedParameter of selectedParameters) {
      for (const conditionReplicate of usedConditionReplicates) {
        const {
          id: replicateId,
          replicateNumber,
          procedureRunCondition: {id: conditionId, name: conditionName},
          procedureMethodExecutionInstanceId,
        } = conditionReplicate;
        const parameterInfos = parameterInfosByPMEI[procedureMethodExecutionInstanceId];
        const matchingParameter = parameterInfos.find((param) => param.paramId === selectedParameter.paramId);
        if (matchingParameter) {
          selectedParametersWithConditions.push({
            ...matchingParameter,
            conditionReplicateInfo: {
              procedureMethodExecutionInstanceId,
              conditionId,
              replicateId,
              conditionName,
              replicateNumber,
            },
          });
        }
      }
    }
    props.onRequestSubmit({
      parameters: selectedParametersWithConditions,
    });
  }
  const parameterSelectionTable = (
    <ParameterSelectionTable
      parameterSettings={parameterSettings}
      selectedParameters={selectedParameters}
      onChangeSelectedParameters={setSelectedParameters}
      parameterTableMode={parameterTableMode}
      isConfig={isConfig}
      parameterUsages={parameterUsages}
      shouldShowValueColumn={includeParameterValues}
    />
  );
  return (
    <Modal
      title={title}
      onRequestSubmit={handleSubmit}
      onRequestClose={handleClose}
      buttons={buttons}
      dataTestAttrs={{component: 'ParameterStructuredTableParameterSelectionModal', element: 'root'}}
    >
      {hasMultipleConditionReplicates ? (
        <StepperV2
          steps={[
            {
              title: 'Select conditions',
              key: 'pick-condition-replicates',
              content: (
                <ConditionReplicateSelectionModalFormControls
                  conditionReplicates={conditionReplicates}
                  selectedConditionReplicateIds={selectedConditionReplicates.map(({id}) => id)}
                  onChangeSelectedConditionReplicateIds={(selectedIds) => {
                    setSelectedConditionReplicates(
                      conditionReplicates.filter(({id}) => selectedIds.includes(id))
                    );
                  }}
                />
              ),
            },
            {
              title: `Select ${PARAMETER_TABLE_MODE_TO_DISPLAY_NAME[parameterTableMode].toLowerCase()}`,
              key: 'pick-params',
              content: parameterSelectionTable,
            },
          ]}
        />
      ) : (
        parameterSelectionTable
      )}
    </Modal>
  );
}
interface ParameterSelectionTableProps extends Pick<
  ParameterStructuredTableParameterSelectionModalProps,
  'fileId' | 'isConfig' | 'parameterTableMode' | 'parameterSettings' | 'parameterUsages'
> {
  parameterSettings: ParameterSettings;
  selectedParameters: Array<ParameterInfo>;
  onChangeSelectedParameters: (params: Array<ParameterInfo>) => void;
  shouldShowValueColumn: boolean;
}

export function ParameterSelectionTable(props: ParameterSelectionTableProps): JSX.Element {
  const {parameterTableMode, parameterUsages, parameterSettings, isConfig} = props;
  const selectedParameterIds = props.selectedParameters.map((param) => param.paramId);
  switch (parameterTableMode) {
    case ParameterStructuredTableMode.PARAMETER_CONFIRMATION:
    case ParameterStructuredTableMode.PARAMETER_ACTUAL: {
      const dataParameters = assembleDataParameterInfos(parameterSettings.dataParameterSettings);
      const validatedParameters = validateAndFilterParameters(dataParameters, {
        parameterTableMode,
        isConfig,
      });
      return (
        <DataParameterSelectionModalFormControls
          allParameters={validatedParameters}
          selectedParameterIds={selectedParameterIds}
          onChangeSelectedParameterIds={(selectedParamIds: Array<string>): void => {
            props.onChangeSelectedParameters(
              validatedParameters.filter((param) => selectedParamIds.includes(param.paramId))
            );
          }}
          parameterUsages={parameterUsages}
          showConfirmationMethod={
            isConfig && parameterTableMode === ParameterStructuredTableMode.PARAMETER_CONFIRMATION
          }
          filtersOutPlateParameter={filtersOutPlateParameters(isConfig, parameterTableMode)}
          shouldShowValueColumn={props.shouldShowValueColumn}
        />
      );
    }
    case ParameterStructuredTableMode.MATERIAL_INPUT_CONFIRMATION: {
      const materialInputs = assembleMaterialInputParameterInfos(parameterSettings.materialInputSettings);
      const validatedParameters = validateAndFilterParameters(materialInputs, {
        parameterTableMode,
        isConfig,
      });
      return (
        <MaterialInputParameterSelectionModalFormControls
          allParameters={validatedParameters}
          selectedParameterIds={selectedParameterIds}
          onChangeSelectedParameterIds={(selectedParamIds: Array<string>): void => {
            props.onChangeSelectedParameters(
              validatedParameters.filter((param) => selectedParamIds.includes(param.paramId))
            );
          }}
          parameterUsages={parameterUsages}
          showConfirmationMethod={isConfig}
          filtersOutPlateParameter={filtersOutPlateParameters(isConfig, parameterTableMode)}
          shouldShowValueColumn={props.shouldShowValueColumn}
        />
      );
    }
    case ParameterStructuredTableMode.MATERIAL_OUTPUT_CONFIRMATION: {
      const materialOutputs = assembleMaterialOutputParameterInfos(parameterSettings.materialOutputSettings);
      const validatedParameters = validateAndFilterParameters(materialOutputs, {
        parameterTableMode,
        isConfig,
      });
      return (
        <MaterialOutputParameterSelectionModalFormControls
          allParameters={validatedParameters}
          selectedParameterIds={selectedParameterIds}
          onChangeSelectedParameterIds={(selectedParamIds: Array<string>): void => {
            props.onChangeSelectedParameters(
              validatedParameters.filter((param) => selectedParamIds.includes(param.paramId))
            );
          }}
          parameterUsages={parameterUsages}
          shouldShowValueColumn={props.shouldShowValueColumn}
        />
      );
    }
    case ParameterStructuredTableMode.EQUIPMENT_CONFIRMATION: {
      const equipment = assembleEquipmentParameterInfos(parameterSettings.equipmentSettings);
      const validatedParameters = validateAndFilterParameters(equipment, {
        parameterTableMode,
        isConfig,
      });
      return (
        <EquipmentParameterSelectionModalFormControls
          allParameters={validatedParameters}
          selectedParameterIds={selectedParameterIds}
          onChangeSelectedParameterIds={(selectedParamIds: Array<string>): void => {
            props.onChangeSelectedParameters(
              validatedParameters.filter((param) => selectedParamIds.includes(param.paramId))
            );
          }}
          parameterUsages={parameterUsages}
          shouldShowValueColumn={props.shouldShowValueColumn}
        />
      );
    }
    default:
      assertNever(parameterTableMode);
  }
  return <div>Unsupported Table Mode used</div>;
}

interface ParameterIds {
  dataParameterIds: Set<string>;
  materialInputIds: Set<string>;
  materialOutputIds: Set<string>;
  equipmentIds: Set<string>;
}

// TODO (BNCH-135315): Use template PMDV rather than plan PMDV
function getParameterIds(psei: PSEINode): ParameterIds {
  assert(
    !isInaccessible(psei.methodExecutionInstance.planMethodDefinitionVersion),
    'Expecting plan method definition version to be accessible'
  );
  const planPMDV = psei.methodExecutionInstance.planMethodDefinitionVersion;
  return {
    dataParameterIds: new Set(
      planPMDV.dataParameterSettings.map(({version}) => version.procedureDataParameterId)
    ),
    materialInputIds: new Set(
      planPMDV.materialInputSettings.map(({version}) => version.procedureMaterialInputId)
    ),
    materialOutputIds: new Set(
      planPMDV.materialOutputSettings.map(({version}) => version.procedureMaterialOutputId)
    ),
    equipmentIds: new Set(planPMDV.equipmentSettings.map(({version}) => version.procedureEquipmentId)),
  };
}

function areParameterIdsEqual(a: ParameterIds, b: ParameterIds): boolean {
  return (
    areSetsEqual(a.dataParameterIds, b.dataParameterIds) &&
    areSetsEqual(a.materialInputIds, b.materialInputIds) &&
    areSetsEqual(a.materialOutputIds, b.materialOutputIds) &&
    areSetsEqual(a.equipmentIds, b.equipmentIds)
  );
}

interface ParameterInfoFilterOptions {
  parameterTableMode: ParameterStructuredTableMode;
  isConfig: boolean;
}

/**
 * Filter out any parameters that should not be shown in the modal
 */
function validateAndFilterParameters<T extends ParameterInfo>(
  parameters: Array<T>,
  {parameterTableMode, isConfig}: ParameterInfoFilterOptions
): Array<T> {
  let filteredParameters = parameters;

  if (!allowsPlateParameters(isConfig, parameterTableMode)) {
    filteredParameters = parameters.filter(
      (param) => param.confirmationMethod === ProcedureParameterConfirmationMethod.TABLE
    );
  }
  return filteredParameters;
}

/** Only allow inserting Plate-confirmed parameters in config Parameter Confirmation and Material Input tables. */
function allowsPlateParameters(isConfig: boolean, parameterTableMode: ParameterStructuredTableMode): boolean {
  return isConfig && PARAMETER_TABLE_MODES_THAT_ALLOW_PLATE_PARAMETERS.includes(parameterTableMode);
}

/** Plate-confirmed parameters are filtered out when inserting into the Parameter Confirmation and Material Input tables in execution. */
function filtersOutPlateParameters(
  isConfig: boolean,
  parameterTableMode: ParameterStructuredTableMode
): boolean {
  return !isConfig && PARAMETER_TABLE_MODES_THAT_ALLOW_PLATE_PARAMETERS.includes(parameterTableMode);
}

/** Get the ParameterSettings from within a given PSEI */
function getParameterSettingsFromPSEI(
  psei: PSEINode,
  includeParameterValues: boolean = true
): ParameterSettings {
  // TODO (BNCH-135315): Use template PMDV rather than plan PMDV
  assert(
    !isInaccessible(psei.methodExecutionInstance.planMethodDefinitionVersion),
    'Expecting plan method definition version to be accessible'
  );
  const basePMDV = psei.methodExecutionInstance.planMethodDefinitionVersion;
  const parameterConfirmationMethodsByParameterId = new Map(
    psei.methodExecutionInstance.parameterConfirmationMethods.map(({parameterId, confirmationMethod}) => [
      parameterId,
      confirmationMethod,
    ])
  );
  const methodVersion = fillPlanMethodVersionWithPlannedValuesAndConfirmationMethods(
    basePMDV,
    includeParameterValues ? psei.methodExecutionInstance.dataParameterPlannedValues : [],
    includeParameterValues ? psei.methodExecutionInstance.materialInputPlannedValues : [],
    includeParameterValues ? psei.methodExecutionInstance.equipmentPlannedValues : [],
    parameterConfirmationMethodsByParameterId,
    // This allows us to get process intermediate amounts off the plan PMDV even when there are not yet any
    // planned values on the PMEI for them.
    {fallbackToPlanMaterialInputs: includeParameterValues}
  );
  return parseParameterSettingsFromNetwork(
    methodVersion,
    (): string => '', // no cid is needed for this modal, it's just to satisfy TS
    {}
  );
}
